import { useEffect, useMemo, useState, useCallback } from "react";
import AppLayout from "../layouts/AppLayout.jsx";
import NavBusinessClass from "../components/NavBusinessClass.jsx";
import EventCard from "../components/EventCard.jsx";
import API from "../config/api.js";

export default function Events() {
  const today = new Date();

  const [events, setEvents] = useState([]);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [loading, setLoading] = useState(true);

  const monthOptions = useMemo(() => {
    const now = new Date();
    const start = new Date(2025, 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    const options = [];
    const current = new Date(start);

    while (current <= end) {
      options.push({
        value: `${current.getFullYear()},${String(
          current.getMonth() + 1
        ).padStart(2, "0")}`,
        label: current.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        }),
        year: current.getFullYear(),
        month: current.getMonth() + 1,
      });

      current.setMonth(current.getMonth() + 1);
    }

    return options.reverse();
  }, []);

  const currentIndex = monthOptions.findIndex(
    (item) => item.year === selectedYear && item.month === selectedMonth
  );

  const currentLabel =
    monthOptions[currentIndex]?.label ||
    new Date(selectedYear, selectedMonth - 1, 1).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("year", selectedYear);
      formData.append("month", String(selectedMonth).padStart(2, "0"));

      const res = await fetch(`${API.events}?list`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await res.json();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error Events:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSelectMonth = (value) => {
    const [year, month] = String(value).split(",");
    setSelectedYear(Number(year));
    setSelectedMonth(Number(month));
  };

  const handlePrevious = () => {
    if (currentIndex < monthOptions.length - 1) {
      const nextItem = monthOptions[currentIndex + 1];
      setSelectedYear(nextItem.year);
      setSelectedMonth(nextItem.month);
    }
  };

  const handleNext = () => {
    if (currentIndex > 0) {
      const prevItem = monthOptions[currentIndex - 1];
      setSelectedYear(prevItem.year);
      setSelectedMonth(prevItem.month);
    }
  };

  const placeholders = Array.from({ length: 3 }, (_, i) => (
    <div key={`placeholder-${i}`} className="col">
      <div className="card rounded-4 h-100 border animate__animated animate__fadeIn animate__slow">
        <div className="card-body">
          <div className="mb-2">
            <span className="placeholder col-3"></span>
          </div>
          <h5 className="card-title">
            <span className="placeholder col-8"></span>
          </h5>
          <p className="card-text">
            <span className="placeholder col-6"></span><br />
            <span className="placeholder col-5"></span><br />
            <span className="placeholder col-4"></span>
          </p>
        </div>
        <div className="card-footer rounded-bottom-4 bg-transparent p-3">
          <button
            type="button"
            className="btn btn-outline-primary disabled placeholder w-100"
            disabled
          >
            Loading...
          </button>
        </div>
      </div>
    </div>
  ));

  return (
    <AppLayout currentPage="events">
      <NavBusinessClass currentPage="events" />

      <div className="row g-4 mb-4 align-items-center">
        <div className="col-md-7 d-none d-sm-block"></div>

        <div className="col-3 col-md-2">
          <a
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-primary w-100"
            href="https://www.addevent.com/calendar/MO365007"
          >
            <span className="d-none d-sm-block">
              <i className="fi fi-rr-plus-small"></i> Follow Calendar
            </span>
            <span className="d-block d-md-none">Follow</span>
          </a>
        </div>

        <div className="col-9 col-md-3">
          <select
            className="d-none"
            id="select-year-month"
            value={`${selectedYear},${String(selectedMonth).padStart(2, "0")}`}
            onChange={(e) => handleSelectMonth(e.target.value)}
          >
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="border rounded">
            <div className="row g-2">
              <div className="col-2">
                <button
                  type="button"
                  className="btn bg-white border-0 w-100"
                  id="btn-next-month-year"
                  onClick={handlePrevious}
                  disabled={currentIndex >= monthOptions.length - 1}
                >
                  <i className="fi fi-rr-angle-left"></i>
                </button>
              </div>

              <div className="col-8">
                <span
                  className="btn bg-white border-0 disabled w-100"
                  id="label-month-year"
                >
                  {currentLabel}
                </span>
              </div>

              <div className="col-2">
                <button
                  type="button"
                  className="btn bg-white border-0 w-100"
                  id="btn-prev-month-year"
                  onClick={handleNext}
                  disabled={currentIndex <= 0}
                >
                  <i className="fi fi-rr-angle-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-2 row-cols-lg-4 row-cols-xxl-4 g-2">
        {loading
          ? placeholders
          : events.map((event) => (
              <div key={event.idevent} className="col">
                <EventCard event={event} />
              </div>
            ))}
      </div>
    </AppLayout>
  );
}